import { useEffect, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { getSettings as getSettigsApi, updateSettings as updateSettingsApi } from 'src/Libs/smart-power/api/smart-power.api'
import { SmartPowerSettingsFormModel, SmartPowerSettingsModel } from 'src/Libs/smart-power/models'
import { settingsFormToDto } from 'src/Libs/smart-power/transformers'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface UseSmartPowerHook {
  isLoading: boolean
  isError: boolean
  smartPowerSettings: SmartPowerSettingsModel | null
  loadSettings: () => Promise<void>
  updateSettings: (data: SmartPowerSettingsFormModel) => Promise<void>
}
const useSmartPower = (storageId?: string): UseSmartPowerHook => {
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const [smartPowerSettings, setSmartPowerSettings] = useState<SmartPowerSettingsModel | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  const loadSettings = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const result = await getSettigsApi(storageId, authFetch)

      if (result.hasErrors) {
        setIsError(true)
        console.warn('Fetching smart power settings resulted in an error')
      }

      setSmartPowerSettings(result.value as SmartPowerSettingsModel)
    } catch (error) {
      console.error(error)
      setIsError(true)
      enqueueErrorStack(t('settings.smart-power.load.error'))
    } finally {
      setIsLoading(false)
    }
  }

  const updateSettings = async (data: SmartPowerSettingsFormModel): Promise<void> => {
    const updateDto = settingsFormToDto(data)
    return updateSettingsApi(storageId, updateDto, authFetch)
  }

  useEffect(() => {
    loadSettings()
  }, [storageId]) // eslint-disable-line

  return { isLoading, isError, smartPowerSettings, loadSettings, updateSettings }
}

export default useSmartPower
